import DarkHorizontalLogo from './logo-dark-horizontal.svg';
import DarkMarkOnlyLogo from './logo-dark-markOnly.svg';
import DarkStackedLogo from './logo-dark-stacked.svg';
import LightHorizontalLogo from './logo-light-horizontal.svg';
import LightMarkOnlyLogo from './logo-light-markOnly.svg';
import LightStackedLogo from './logo-light-stacked.svg';
import OnPrimaryHorizontalLogo from './logo-onPrimary-horizontal.svg';
import OnPrimaryMarkOnlyLogo from './logo-onPrimary-markOnly.svg';
import OnPrimaryStackedLogo from './logo-onPrimary-stacked.svg';

import { SvgIcon, useTheme } from '@mui/material';
import tokens from '@verifime/design-tokens';
import { FC, SVGAttributes } from 'react';

export type TLogoColorMode = 'light' | 'dark' | 'onPrimary';
// The prop callers actually pass. 'auto' is not a rendered mode itself (ICON_MAPPINGS has no
// 'auto' artwork): it is resolved to 'light' or 'dark' from the ambient theme before ICON_MAPPINGS
// is indexed. Kept separate from TLogoColorMode so ICON_MAPPINGS (a real colorMode -> asset map)
// cannot be indexed by a value it has no asset for.
export type TLogoColorModeProp = TLogoColorMode | 'auto';
export type TLogoSize = 'small' | 'medium' | 'large';
export type TLogoLayout = 'markOnly' | 'horizontal' | 'stacked';
// SVGSVGElement, not SVGAElement (the DOM interface for SVG's <a> hyperlink element): Logo renders
// an <svg>. This went unnoticed while every icon came from a real .svg import, because custom.d.ts
// types those as FC<SVGAttributes<SVGElement>>, the base interface, which is contravariantly
// assignable to any element subtype including the wrong one. It matters now that TLogoAssetSet is
// a public contract: a hand-written component naming a concrete element type (as any real portal's
// logo component will) only satisfies this if the element type named here is the right one.
//
// viewbox lives here, per colour mode, rather than once per layout: it is a property of the
// individual .svg file's own coordinate system, not of the layout. This holds for the built-in
// nine only by coincidence (every horizontal file happens to be 195x64, every stacked 140x128,
// every markOnly 64x64); it does not hold for a real caller-supplied set, where a portal's
// horizontal artwork can differ in intrinsic size between colour modes (e.g. one asset per
// colour mode is a different production, not a recolour of the same drawing). A wrong viewBox
// distorts or crops the rendered logo (see the SVGO/removeViewBox background above), which is the
// exact defect #2220 and #2263 each fixed for one component; keeping viewbox keyed by colour mode
// is what stops it recurring for a third.
type TIconAsset = { component: FC<SVGAttributes<SVGSVGElement>>; viewbox: string };
type TIconMapping = { [key in TLogoColorMode]: TIconAsset };
// size, unlike viewbox, stays keyed by layout alone: it is a design decision about how large this
// layout renders at small/medium/large, which does not depend on which colour the artwork happens
// to be. Keeping it here also avoids repeating an identical three-entry size map once per colour
// mode in ICON_MAPPINGS below.
type TSizeMapping = { [key in TLogoSize]: string };
type TSvgIconMapping = {
  [key in TLogoLayout]: { icon: TIconMapping; size: TSizeMapping };
};
// The shape a portal declares its own brand artwork with (see
// packages/components/docs/design-decisions/0002-logo-asset-set-as-a-prop.md). Unlike
// TSvgIconMapping, a layout may be omitted entirely (a set can be horizontal-only) and, within a
// declared layout, a colour mode may be omitted too (the same real-world set can carry all three
// colour modes for one layout and only one for another). ICON_MAPPINGS is a TSvgIconMapping, which
// is total in both axes, so it satisfies this narrower requirement and still typechecks as a
// TLogoAssetSet without any change to its own declaration.
export type TLogoAssetSet = {
  [key in TLogoLayout]?: { icon: Partial<TIconMapping>; size: TSizeMapping };
};

// make sure each colour mode's viewbox matches its own svg file's viewBox.
// This is important to ensure the icons can be scaled properly.
// Exported (not just used internally) so Logo.test.tsx can assert this mapping against the
// source .svg files on disk, rather than trusting the value never drifts.
export const ICON_MAPPINGS: TSvgIconMapping = {
  horizontal: {
    icon: {
      light: { component: LightHorizontalLogo, viewbox: '0 0 195 64' },
      dark: { component: DarkHorizontalLogo, viewbox: '0 0 195 64' },
      onPrimary: { component: OnPrimaryHorizontalLogo, viewbox: '0 0 195 64' },
    },
    size: {
      small: tokens.size7xs,
      medium: tokens.size6xs,
      large: tokens.size4xs,
    },
  },
  stacked: {
    icon: {
      light: { component: LightStackedLogo, viewbox: '0 0 140 128' },
      dark: { component: DarkStackedLogo, viewbox: '0 0 140 128' },
      onPrimary: { component: OnPrimaryStackedLogo, viewbox: '0 0 140 128' },
    },
    size: {
      small: tokens.size5xs,
      medium: tokens.size4xs,
      large: tokens.size3xs,
    },
  },
  markOnly: {
    icon: {
      light: { component: LightMarkOnlyLogo, viewbox: '0 0 64 64' },
      dark: { component: DarkMarkOnlyLogo, viewbox: '0 0 64 64' },
      onPrimary: { component: OnPrimaryMarkOnlyLogo, viewbox: '0 0 64 64' },
    },
    size: {
      small: tokens.size6xs,
      medium: tokens.size5xs,
      large: tokens.size4xs,
    },
  },
};

// A caller-supplied assetSet may not have artwork for the requested layout/colorMode combination
// (see TLogoAssetSet above). Falling back to the built-in VerifiMe artwork here would render the
// wrong brand while looking correct, which is worse than a crash because it ships silently, so
// this throws instead. See packages/components/docs/design-decisions/0002-logo-asset-set-as-a-prop.md.
function resolveLogoAsset(
  assetSet: TLogoAssetSet,
  layout: TLogoLayout,
  colorMode: TLogoColorMode,
): { component: FC<SVGAttributes<SVGSVGElement>>; size: TSizeMapping; viewbox: string } {
  const layoutAssets = assetSet[layout];
  const iconAsset = layoutAssets?.icon[colorMode];

  // iconAsset is already derived with optional chaining, so it is undefined whenever layoutAssets
  // is: the `!layoutAssets` check never changes which branch this takes. It is here only so
  // TypeScript narrows layoutAssets to defined below, where layoutAssets.size is read;
  // `!iconAsset` alone would leave layoutAssets typed as possibly undefined at that point.
  if (!layoutAssets || !iconAsset) {
    throw new Error(
      `Logo: the supplied assetSet has no artwork for layout "${layout}" and colorMode "${colorMode}".`,
    );
  }

  return { component: iconAsset.component, size: layoutAssets.size, viewbox: iconAsset.viewbox };
}

export function Logo({
  size = 'medium',
  layout = 'horizontal',
  colorMode = 'light',
  assetSet = ICON_MAPPINGS,
}: {
  /**
   * 'light' | 'dark' | 'onPrimary' pick a specific rendered mode directly, exactly as before.
   * 'auto' follows the active MUI theme mode (theme.palette.mode), so a caller placing the logo
   * on the page background no longer has to read that theme value and pass it through itself.
   * 'onPrimary' stays reachable directly: it puts the logo on a brand-coloured panel regardless
   * of theme mode, which is the caller's own layout choice, not something the theme mode implies.
   */
  colorMode?: TLogoColorModeProp;
  layout?: TLogoLayout;
  size?: TLogoSize;
  /**
   * The artwork to render. Defaults to the built-in VerifiMe lockups (ICON_MAPPINGS), so every
   * existing call site is unchanged. A portal that owns a different brand lockup (e.g.
   * customer-portal's Wallet artwork) passes its own set here instead of reimplementing Logo's
   * SvgIcon/size/viewBox mechanism itself.
   */
  assetSet?: TLogoAssetSet;
}) {
  const theme = useTheme();
  const resolvedColorMode: TLogoColorMode = colorMode === 'auto' ? theme.palette.mode : colorMode;

  const {
    component,
    size: sizeMapping,
    viewbox,
  } = resolveLogoAsset(assetSet, layout, resolvedColorMode);

  return (
    <SvgIcon
      sx={{
        width: 'auto',
        height: sizeMapping[size],
        fontSize: 'inherit',
      }}
      viewBox={viewbox}
      component={component}
    />
  );
}
